/**
 * Redux witness watchers for content policy
 * @module services/content-policy
 */

import { map as mapPromise } from 'bluebird'
import { List, Map } from 'immutable'
import { showSuccessToast, showErrorToast } from 'services/toast/redux/actions'
import { SET_PAGE_READY } from '../pages/redux/actions'
import { isLoggedIn } from '../auth'
import { SET_AUTH_DATA } from '../auth/redux/actions'
import { isPageReady } from '../pages'
import { SET_LOCATION_DATA, setLocation as setLocationAction } from '../location/redux/actions'
import {
  createViewRecords,
  findView,
  getApplicationDataByKey,
  getViewRecordsAsList,
} from '../data'
import {
  GET_DATA_LIST,
  getDataList as getDataListAction,
  setDataList as setDataListAction,
  deleteDataApplicationByKey as deleteDataApplicationByKeyAction,
  setDataApplicationByKey as setDataApplicationByKeyAction,
  PERSIST_DATA,
  SET_DATA_LIST,
  resetData as resetDataAction,
} from '../data/redux/actions'
import { getConfigDataServer } from '../config'
import {
  CONTENT_POLICY_STORE_KEY,
  CONTENT_POLICY_DETAIL_APPLICATION_KEY,
  CONTENT_POLICY_CREATE_APPLICATION_KEY,
  isContentPolicyListPage,
  isContentPolicyDetailPage,
  createContentPolicy,
  getContentPolicyList,
  createContentPolicyListViewOptions,
  createContentPolicyDetailViewOptions,
  createContentPolicyCreateViewOptions,
  isContentPolicyCreatePage,
  removeContentPolicy,
  updateContentPolicy,
} from '.'
import { BULK_DELETE_CONTENT_POLICY, DELETE_CONTENT_POLICY } from './redux/actions'

/**
 * Returns true if the action has a storeKey of 'content'
 * @param {Object} options The parameters
 * @param {Object} [options.action={}] A redux action
 * @param {Object} [options.action.payload] The action payload
 * @param {String} [options.action.payload.storeKey] The store key
 * @returns {Boolean} True when store key equals content
 */
function isContentPolicyDataAction (options = {}) {
  const { action = {} } = options
  const { payload: { storeKey } = {} } = action
  return storeKey === CONTENT_POLICY_STORE_KEY
}

export function initContentPolicyListPage ({ after }) {
  return after(
    [SET_AUTH_DATA, SET_PAGE_READY, SET_LOCATION_DATA],
    ({ state, prevState, dispatch }) => {
      const { data, location } = state
      const refreshView = location.getIn(['data', 'query', 'refreshView'])
      const storeKey = CONTENT_POLICY_STORE_KEY
      const options = createContentPolicyListViewOptions(state)
      const prevOptions = createContentPolicyListViewOptions(prevState)
      const view = findView({ data, storeKey, options })
      const contentReady = view && options.equals(prevOptions) && !refreshView
      if (contentReady) {
        return
      }
      dispatch(getDataListAction({
        storeKey,
        options,
      }))
    },
  ).when(({ state }) => {
    const { auth, page } = state
    return isLoggedIn(auth) && isPageReady({ page }) && isContentPolicyListPage(state)
  })
}

/**
 * Initialize content policy detail page
 * @param {*} param0 The params
 */
export function initContentPolicyDetailPage ({ after }) {
  return after(
    [SET_AUTH_DATA, SET_PAGE_READY, SET_LOCATION_DATA],
    ({ dispatch, state, prevState }) => {
      const { data } = state
      const storeKey = CONTENT_POLICY_STORE_KEY
      const options = createContentPolicyDetailViewOptions(state)
      const prevOptions = createContentPolicyDetailViewOptions(prevState)
      const view = findView({ data, storeKey, options })
      const viewOptionsChanged = !view || !options.equals(prevOptions)
      if (options.get('policyId') && prevOptions.get('policyId') && options.get('policyId') !== prevOptions.get('policyId')) {
        dispatch(deleteDataApplicationByKeyAction({
          storeKey,
          applicationKey: CONTENT_POLICY_DETAIL_APPLICATION_KEY,
        }))
      }
      if (viewOptionsChanged) {
        dispatch(getDataListAction({
          storeKey,
          options,
        }))
      }
    },
  ).when(({ state }) => {
    const { auth, page } = state
    return isLoggedIn(auth) && isPageReady({ page }) && isContentPolicyDetailPage(state)
  })
}

export function setDataListContentDetailPage ({ after }) {
  return after([SET_DATA_LIST], ({ dispatch, state, action }) => {
    const { payload: { applicationKey = CONTENT_POLICY_DETAIL_APPLICATION_KEY } } = action
    const { data } = state
    const storeKey = CONTENT_POLICY_STORE_KEY
    const options = createContentPolicyDetailViewOptions(state)
    const record = getViewRecordsAsList({
      data,
      storeKey,
      options,
    }).first() || Map()
    const applicationData = getApplicationDataByKey({
      data, storeKey, applicationKey,
    }) || Map()
    const idsEqual = record.get('policyId') === applicationData.get('policyId')
    if (!record.isEmpty() && !idsEqual) {
      dispatch(setDataApplicationByKeyAction({
        storeKey,
        applicationKey,
        applicationData: record,
      }))
    }
  }).when((options = {}) => {
    const { state } = options
    const { auth, page } = state
    return isLoggedIn(auth)
      && isPageReady({ page })
      && isContentPolicyDetailPage(state)
      && isContentPolicyDataAction(options)
  })
}

export function getDataListContentPolicy ({ after }) {
  return after(GET_DATA_LIST, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, options } = action.payload
    try {
      const contentPolicyList = await getContentPolicyList({
        auth,
        server: getConfigDataServer(config),
        offset: options.get('offset'),
        limit: options.get('limit'),
        policyId: options.get('policyId'),
        sort: options.get('sort'),
        sortDirection: options.get('sortDirection'),
      })
      dispatch(setDataListAction({
        storeKey,
        options,
        records: createViewRecords(contentPolicyList.get('contentPolicies'), 'policyId'),
        total: contentPolicyList.get('totalCount'),
        processing: false,
      }))
    } catch (e) {
      dispatch(setDataListAction({
        storeKey,
        options,
        processing: false,
        error: e,
      }))
    }
  })
    .when(isContentPolicyDataAction)
}

/**
 * Persist data for content policy create to backend
 */
export function persistDataContentPolicy ({ after }) {
  return after(PERSIST_DATA, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, options, record } = action.payload
    try {
      const isCreate = options.get('create')
      let contentPolicy
      if (isCreate) {
        contentPolicy = await createContentPolicy({
          auth,
          server: getConfigDataServer(config),
          data: record,
        })
      } else {
        contentPolicy = await updateContentPolicy({
          auth,
          server: getConfigDataServer(config),
          data: record,
          generatePath: options.get('generatePath'),
        })
      }
      dispatch(showSuccessToast('Content policy Saved'))
      dispatch(setDataListAction({
        storeKey,
        options,
        records: createViewRecords(List([contentPolicy])),
        total: 1,
        processing: false,
      }))
      if (isCreate) {
        dispatch(deleteDataApplicationByKeyAction({
          storeKey,
          applicationKey: CONTENT_POLICY_CREATE_APPLICATION_KEY,
        }))
        dispatch(resetDataAction({
          storeKey: CONTENT_POLICY_STORE_KEY,
        }))
      }
      dispatch(setLocationAction({
        location: {
          href: '/content-policy?refreshView=1',
        },
      }))
      window.history.pushState(null, null, '/content-policy')
    } catch (e) {
      dispatch(showErrorToast('Content policy failed to save'))
      dispatch(setDataListAction({
        storeKey,
        options,
        processing: false,
        error: e,
      }))
    }
  })
    .when(isContentPolicyDataAction)
}

/**
 * Initialize content policy create page
 * @param {*} param0 The params
 */
export function initContentPolicyCreatePage ({ after }) {
  return after(
    [SET_AUTH_DATA, SET_PAGE_READY, SET_LOCATION_DATA],
    ({ dispatch, state, prevState }) => {
      const { data } = state
      const storeKey = CONTENT_POLICY_STORE_KEY
      const applicationKey = CONTENT_POLICY_CREATE_APPLICATION_KEY
      const options = createContentPolicyCreateViewOptions(state)
      const prevOptions = createContentPolicyCreateViewOptions(prevState)
      const view = findView({ data, storeKey, options })
      const dataReady = view && options.equals(prevOptions)
      if (dataReady) {
        return
      }
      const applidationData = getApplicationDataByKey({
        data, storeKey, applicationKey,
      }) || Map()
      if (!applidationData.isEmpty()) {
        // Clear out any previous application data
        dispatch(deleteDataApplicationByKeyAction({
          storeKey, applicationKey,
        }))
      }
    },
  ).when(({ state }) => {
    const { auth, page } = state
    return isLoggedIn(auth) && isPageReady({ page }) && isContentPolicyCreatePage(state)
  })
}

export function deleteContentPolicy ({ after }) {
  return after(DELETE_CONTENT_POLICY, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, policyId } = action.payload

    dispatch(setDataListAction({
      storeKey,
      processing: true,
    }))
    try {
      await removeContentPolicy({
        auth,
        server: getConfigDataServer(config),
        policyId,
      })
      dispatch(setLocationAction({
        location: {
          href: '/content-policy?refreshView=1',
        },
      }))
      dispatch(showSuccessToast('Content policy deleted'))
      window.history.pushState(null, null, '/content-policy')
      const options = createContentPolicyListViewOptions(state)
      dispatch(getDataListAction({
        storeKey,
        options,
      }))
    } catch (e) {
      dispatch(setDataListAction({
        storeKey,
        processing: false,
        error: e,
      }))
      dispatch(showErrorToast('Content policy delete failed'))
    }
  })
}

// TODO: maybe it would be greate to have bulk delete endpoint on BE side
export function deleteContentPolicyBulk ({ after }) {
  return after(BULK_DELETE_CONTENT_POLICY, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, policyIds } = action.payload

    dispatch(setDataListAction({
      storeKey,
      processing: true,
    }))
    try {
      await mapPromise(policyIds, async (policyId) => removeContentPolicy({
        auth,
        server: getConfigDataServer(config),
        policyId,
      }))
      dispatch(showSuccessToast('Content policy bulk delete completed'))
      window.location.reload()
    } catch (e) {
      dispatch(setDataListAction({
        storeKey,
        processing: false,
        error: e,
      }))
      dispatch(showErrorToast('Content policy bulk delete failed'))
    }
  })
}
